import React from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@vaiu/ui/card";
import { Button } from "@vaiu/ui/button";
import { Badge } from "@vaiu/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@vaiu/ui/avatar";
import { Separator } from "@vaiu/ui/separator";
import { ArrowLeft, Building2, Calendar, DollarSign, TrendingUp, TrendingDown } from 'lucide-react';
import { i18n } from '@/utils/i18n';

const BusinessDetail = () => {
  const navigate = (path: string) => console.log('Navigate to:', path);

  // Mock business data - in real app this would come from route params
  const business = {
    id: 1,
    name: 'Coffee Corner',
    type: 'Restaurant',
    totalTransactions: 12,
    totalAmount: 156.50,
    lastTransaction: 'Yesterday'
  };

  // Mock transaction history
  const transactions = [
    { id: 1, date: '2024-06-10', amount: 12.50, type: 'payment', description: 'Latte & Croissant' },
    { id: 2, date: '2024-06-08', amount: 8.75, type: 'payment', description: 'Cappuccino' },
    { id: 3, date: '2024-06-05', amount: 15.20, type: 'payment', description: 'Lunch combo' },
    { id: 4, date: '2024-06-03', amount: 22.00, type: 'payment', description: 'Team coffee order' },
    { id: 5, date: '2024-06-01', amount: 9.50, type: 'payment', description: 'Americano & muffin' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-md mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center space-x-3 pt-4">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/contacts')}
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold">{business.name}</h1>
            <p className="text-muted-foreground">{business.type}</p>
          </div>
        </div>

        {/* Business Summary */}
        <Card className="bg-gradient-to-r from-primary to-primary/90 text-primary-foreground">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center space-x-2 text-lg">
              <Building2 className="w-5 h-5" />
              <span>{i18n.__('businessDetail.transactionSummary')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm opacity-90">{i18n.__('businessDetail.totalTransactions')}</span>
              <span className="text-xl font-bold">{business.totalTransactions}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm opacity-90">{i18n.__('businessDetail.totalAmount')}</span>
              <span className="text-xl font-bold">${business.totalAmount}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm opacity-90">{i18n.__('businessDetail.lastTransaction')}</span>
              <span className="text-sm font-medium">{business.lastTransaction}</span>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4">
          <Button className="h-12">
            <DollarSign className="w-4 h-4 mr-2" />
            {i18n.__('businessDetail.payAgain')}
          </Button>
          <Button variant="outline" className="h-12">
            <Calendar className="w-4 h-4 mr-2" />
            {i18n.__('businessDetail.schedule')}
          </Button>
        </div>

        {/* Transaction History */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{i18n.__('businessDetail.transactionHistory')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {transactions.map((transaction) => (
              <div key={transaction.id} className="flex items-center justify-between p-3 rounded-lg border">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                    <TrendingDown className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">{transaction.description}</p>
                    <p className="text-sm text-muted-foreground">{transaction.date}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-green-600">-${transaction.amount}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BusinessDetail;
